import { apiClient } from "@/services/api"
import { defineStore } from "pinia"
import { hasMoreRows } from "@/services/listAnswer"

let lastPage = -1

export const MACHINE_FORM_NAMES = {
  GENERAL: "general",
  FILES: "files",
  ACTIVITY: "activity",
}

const _blankMachine = () => {
  return {
    id: "",
    name: "",
    unit: null,
    legacyUnit: null,
    purchasePrices: [],
    purchasePriceVariants: [],
    note: null,
    files: null,
    createdAt: null,
    createdBy: null,
    lastModifiedAt: null,
    lastModifiedBy: null,
  }
}

export const useMachineStore = defineStore("machine", {
  state: () => ({
    machines: [],
    machine: _blankMachine(),
  }),
  getters: {
    getMachines: state => state.machines,
    getMachine: state => state.machine,
  },
  actions: {
    setMachines(machines) {
      this.machines = [...machines]
    },
    addMachines(machines) {
      machines.forEach(machine => this.machines.push(machine))
    },
    clearMachines() {
      this.machines = []
    },
    setMachine(machine) {
      this.machine = machine
    },
    clearMachine() {
      this.machine = _blankMachine()
    },
    updateMachine(machine) {
      if (machine.purchasePrices && machine.purchasePrices.length <= 0) {
        machine.purchasePrices = null
      }
      return apiClient
        .put(`/machine/${machine.id}`, machine, { headers: { tenant: true, authorization: true } })
        .then(res => {
          if (res.status === 200) {
            this.setMachine(res.data)
          }
          return res
        })
    },
    createMachine(machine, files = []) {
      if (machine.purchasePrices && machine.purchasePrices.length <= 0) {
        machine.purchasePrices = null
      }
      if (files && files.length > 0) {
        const formData = new FormData()
        formData.append('machine', new Blob([JSON.stringify(machine)], { type: 'application/json' }))
        files.forEach(file => formData.append('files', file))
        return apiClient.post('/machine/with-files', formData, { headers: { tenant: true, authorization: true } })
          .then(res => {
            if (res.status === 200) this.setMachines(res.data)
            return res
          })
      }
      return apiClient.post("/machine", machine, { headers: { tenant: true, authorization: true } }).then(res => {
        if (res.status === 200) {
          this.setMachines(res.data)
        }
        return res
      })
    },
    loadMachine(id) {
      return apiClient.get(`/machine/${id}`, { headers: { tenant: true, authorization: true } }).then(res => {
        if (res.status === 200) {
          if (res.data.purchasePrices == null) {
            res.data.purchasePrices = []
          }
          this.setMachine(res.data)
        }
        return res
      })
    },
    loadMachines({ page, search, sortBy, sortDesc, selected, itemsPerPage, archive }) {
      return apiClient
        .get("/machine", {
          params: {
            // Unset for the records in use; `ARCHIVED` for the overview's archive (docs/archiving.md).
            archive,
            page: page,
            search: search,
            sortBy: sortBy,
            sortDesc: sortDesc,
            selected: selected,
            itemsPerPage: itemsPerPage,
          },
          headers: { tenant: true, authorization: true },
        })
        .then(res => {
          if (res.status === 200) {
            if (page === 0) {
              this.setMachines(res.data.content)
            } else if (lastPage === page) {
              this.setMachines(res.data.content)
            } else if (lastPage === page - 1) {
              this.addMachines(res.data.content)
            } else {
              this.setMachines(res.data.content)
            }
            lastPage = page
          }
          return res
        })
    },
    /**
     * A page of the catalogue for a picker (`LewAutocompleteCatalog`), returned rather than written
     * into the store: the store's list is the machine overview's rows, and a picker's search hits replaced them.
     * Silent, as every picker's search is: a snackbar per keystroke helps nobody.
     */
    searchMachines({ search, page = 0, itemsPerPage }) {
      return apiClient
        .get("/machine", {
          params: { page, itemsPerPage, search: search || undefined },
          headers: { tenant: true, authorization: true },
          silent: true,
        })
        .then(res => ({ items: res.data?.content ?? [], hasMore: hasMoreRows(res.data, itemsPerPage) }))
    },
  },
})
